import {Box, Grid, Section, type AnimateProps, type GridColumnIndex} from '@primer/react-brand/esm'
import {clsx} from 'clsx'

import type {PrimerCards} from '../../../schemas/contentful/contentTypes/primerCards'

import {ContentfulCard, type ContentfulCardProps} from '../ContentfulCard/ContentfulCard'

import styles from './ContentfulCards.module.css'

/**
 * Uses TypeScript discriminated union to ensure the component accepts either a `component`
 * or a `cards` prop, but not both. This increases the flexibility
 * of this component by supporting either a primerCards entry or a
 * plain array of primerComponentCard entries.
 */
export type ContentfulCardsProps = {
  className?: string
  hasBorder?: boolean
  fullWidth?: boolean
  animate?: AnimateProps
} & Pick<ContentfulCardProps, 'imageAspectRatio' | 'ctaVariant'> &
  ({variant?: 'default'; spanOpts?: {[key: string]: GridColumnIndex}} | {variant: 'gridline'; spanOpts?: never}) &
  (
    | {
        cards?: never
        component: PrimerCards
      }
    | {
        cards: PrimerCards['fields']['cards']
        component?: never
      }
  )

export function ContentfulCards({
  cards,
  component,
  hasBorder,
  fullWidth,
  animate,
  imageAspectRatio,
  ctaVariant,
  className,
  spanOpts,
  variant,
}: ContentfulCardsProps) {
  const collection = component !== undefined ? component.fields.cards : cards
  const isGridline = (variant ?? component?.fields.variant) === 'gridline'
  const resolvedFullWidth = fullWidth ?? component?.fields.fullWidth

  const desktop = collection.length > 2 ? 4 : 6
  const wideColumnCount = 12 / desktop
  const lastRowStart = Math.floor((collection.length - 1) / wideColumnCount) * wideColumnCount

  const span: {[key: string]: GridColumnIndex} = {
    xsmall: 12,
    small: 12,
    medium: 6,
    large: desktop,
    xlarge: desktop,
    xxlarge: desktop,
    ...spanOpts,
  }

  const hasMinimalCard = collection.some(item => item?.fields?.variant === 'minimal')

  const grid = (
    <Grid
      className={clsx(className, !isGridline && hasMinimalCard && styles.withMinimalCard)}
      columnGap={isGridline ? 'none' : undefined}
      rowGap={isGridline ? 'none' : undefined}
      enableGutters={!isGridline}
    >
      {collection.map((card, index) => (
        <Grid.Column
          span={isGridline ? {xsmall: 12, xlarge: desktop} : span}
          key={card.sys.id}
          className={
            isGridline
              ? clsx(
                  styles.gridlineColumn,
                  index >= lastRowStart && styles.lastRow,
                  ((index + 1) % wideColumnCount === 0 || index === collection.length - 1) && styles.rowEnd,
                )
              : undefined
          }
        >
          <Box animate={animate} className={clsx('height-full', isGridline && styles.gridlineCard)}>
            <ContentfulCard
              component={card}
              variant={card.fields.variant ?? (isGridline ? 'minimal' : undefined)}
              hasBorder={isGridline ? false : (card.fields.hasBorder ?? hasBorder)}
              fullWidth={resolvedFullWidth}
              imageAspectRatio={imageAspectRatio}
              ctaVariant={ctaVariant}
            />
          </Box>
        </Grid.Column>
      ))}
    </Grid>
  )

  return isGridline ? (
    <Section as="div" variant="gridline" fullWidth paddingBlockStart="none" paddingBlockEnd="none">
      {grid}
    </Section>
  ) : (
    grid
  )
}
